import { useState, useEffect } from "react";
import { Box, Typography, Button, Modal, TextField, MenuItem, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert, Autocomplete, Chip } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

import { getData, insertData, deleteData, updateData } from "../api";
import { useNavigate } from "react-router-dom"; // Import de `useNavigate`


interface Video {
  id: number;
  title: string;
}

export default function ManageExercises() {
  const [exercises, setExercises] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [videos, setVideos] = useState<Video[]>([]); // Typage des vidéos
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentExercise, setCurrentExercise] = useState({
    id: null,
    title: "",
    description: "",
    category_id: "",
    max_points: 5,
    repetitions: 5,
    recommended_videos: [] as string[], // Typage des vidéos recommandées
  });
  const [formErrors, setFormErrors] = useState({
    title: false,
    category_id: false,
    recommended_videos: false,
  });
  const navigate = useNavigate(); // Initialisation de `useNavigate`


  useEffect(() => {
    getData("exercises").then((res: any) => {
      setExercises(res.data || []);
    });
    getData("exercise_categories").then((res: any) => {
      setCategories(res.data || []);
    });
    getData("videos").then((res: any) => {
      setVideos(res.data || []);
    });
  }, []);

  const validateForm = () => {
    const errors = {
      title: !currentExercise.title.trim(),
      category_id: !currentExercise.category_id,
      recommended_videos: currentExercise.recommended_videos.length === 0,
    };
    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleOpenModal = (exercise = null) => {
    if (exercise) {
      setCurrentExercise(exercise);
    } else {
      setCurrentExercise({
        id: null,
        title: "",
        description: "",
        category_id: "",
        max_points: 5,
        repetitions: 5,
        recommended_videos: [],
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenDeleteModal = (id: string) => {
    setExerciseToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setExerciseToDelete(null);
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = () => {
    if (exerciseToDelete) {
      deleteData("exercises", `id = ${exerciseToDelete}`, true)
        .then(() => {
          setExercises(exercises.filter((exercise) => exercise.id !== exerciseToDelete));
          handleCloseDeleteModal();
        })
        .catch((error) => {
          console.error("Erreur lors de la suppression :", error);
          setErrorMessage(error.message || "Une erreur inconnue est survenue.");
          handleCloseDeleteModal();
        });
    }
  };

  const handleSaveExercise = () => {
    if (!validateForm()) {
      setErrorMessage("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    if (currentExercise.id) {
      updateData("exercises", currentExercise, `id = ${currentExercise.id}`).then(() => {
        getData("exercises").then((res: any) => {
          setExercises(res.data || []);
        });
      });
    } else {
      insertData("exercises", currentExercise).then(() => {
        getData("exercises").then((res: any) => {
          setExercises(res.data || []);
        });
      });
    }
    handleCloseModal();
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate("/dashboard")}
          sx={{ marginBottom: 4 }}
        >
          ← Retour au Tableau de bord
        </Button>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Gestion des exercices
      </Typography>
      <Button variant="contained" onClick={() => handleOpenModal()} sx={{ marginBottom: 4 }}>
        Ajouter un exercice
      </Button>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
        {exercises.map((exercise) => {
          const cat = categories.find((c: any) => c.id === exercise.category_id);
          let recVids: string[] = [];
          try {
            if (Array.isArray(exercise.recommended_videos)) {
              recVids = exercise.recommended_videos.map((v: any) => v.toString());
            } else if (typeof exercise.recommended_videos === 'string') {
              const parsed = JSON.parse(exercise.recommended_videos || '[]');
              recVids = Array.isArray(parsed) ? parsed.map((v: any) => v.toString()) : [];
            }
          } catch {
            recVids = [];
          }
          const recTitles: string[] = recVids.map((vidId: string) => {
            const v = videos.find((vv) => vv.id.toString() === vidId);
            return v ? v.title : `Vidéo #${vidId}`;
          });
          return (
            <Box key={exercise.id} sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{exercise.title}</Typography>
                {cat && <Chip label={cat.name} color="secondary" size="small" />}
              </Box>
              {exercise.description && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>{exercise.description}</Typography>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={`Points max: ${exercise.max_points}`} size="small" />
                <Chip label={`Répétitions: ${exercise.repetitions}`} size="small" />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {recTitles.length > 0 ? (
                  recTitles.slice(0, 4).map((title: string, idx: number) => (
                    <Chip key={`${exercise.id}-vid-${idx}`} label={title} variant="outlined" size="small" />
                  ))
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>Aucune vidéo recommandée</Typography>
                )}
                {recTitles.length > 4 && (
                  <Chip label={`+${recTitles.length - 4}`} size="small" />
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={() => handleOpenModal(exercise)} startIcon={<Edit />}>Modifier</Button>
                <Button size="small" variant="contained" onClick={() => handleOpenModal(exercise)}>Associer des vidéos</Button>
                <Button size="small" color="error" variant="outlined" onClick={() => handleOpenDeleteModal(exercise.id)} startIcon={<Delete />}>Supprimer</Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Modal open={modalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            {currentExercise.id ? "Modifier l'exercice" : "Ajouter un exercice"}
          </Typography>
          <TextField
            label="Titre"
            fullWidth
            value={currentExercise.title}
            onChange={(e) => setCurrentExercise({ ...currentExercise, title: e.target.value })}
            error={formErrors.title}
            helperText={formErrors.title ? "Le titre est obligatoire." : ""}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={currentExercise.description}
            onChange={(e) => setCurrentExercise({ ...currentExercise, description: e.target.value })}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Catégorie"
            select
            fullWidth
            value={currentExercise.category_id}
            onChange={(e) => setCurrentExercise({ ...currentExercise, category_id: e.target.value })}
            error={formErrors.category_id}
            helperText={formErrors.category_id ? "La catégorie est obligatoire." : ""}
            sx={{ marginBottom: 2 }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Points maximum"
            type="number"
            fullWidth
            value={currentExercise.max_points}
            onChange={(e) => setCurrentExercise({ ...currentExercise, max_points: Number(e.target.value) })}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Répétitions"
            type="number"
            fullWidth
            value={currentExercise.repetitions}
            onChange={(e) => setCurrentExercise({ ...currentExercise, repetitions: Number(e.target.value) })}
            sx={{ marginBottom: 2 }}
          />
          <Autocomplete
            multiple
            options={videos}
            getOptionLabel={(option) => option.title}
            value={videos.filter((video) =>
              currentExercise.recommended_videos.includes(video.id.toString())
            )}
            onChange={(event, newValue) => {
              setCurrentExercise({
                ...currentExercise,
                recommended_videos: newValue.map((video) => video.id.toString()),
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Vidéos recommandées"
                error={formErrors.recommended_videos}
                helperText={
                  formErrors.recommended_videos
                    ? "Veuillez sélectionner au moins une vidéo."
                    : ""
                }
              />
            )}
            sx={{ marginBottom: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal} color="secondary">
              Annuler
            </Button>
            <Button onClick={handleSaveExercise} variant="contained">
              Enregistrer
            </Button>
          </Box>
        </Box>
      </Modal>

      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer cet exercice ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
